// src/contexts/DealershipContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef  } from 'react';
import UserContext from './UserContext';
import { useAuth } from './AuthProvider';
import { parseBAC, bacToString } from '../utils/bacUtils';

// [Shipping] BEGIN
const SHIPPING_DEFAULTS = {
  shippingSameAsDealership: false,
  shippingAddress1: null,
  shippingAddress2: null,
  shippingCity: null,
  shippingState: null,
  shippingPostalCode: null,
  shippingCountry: null,
};

const normalizeDealership = (d = {}) => ({
  ...d,
  ...SHIPPING_DEFAULTS,
  shippingSameAsDealership: d?.shippingSameAsDealership ?? false,
  shippingAddress1: d?.shippingAddress1 ?? null,
  shippingAddress2: d?.shippingAddress2 ?? null,
  shippingCity: d?.shippingCity ?? null,
  shippingState: d?.shippingState ?? null,
  shippingPostalCode: d?.shippingPostalCode ?? null,
  shippingCountry: d?.shippingCountry ?? null,
});

const formatShippingAddress = (dealership) => {
  if (!dealership) return '';
  if (dealership.shippingSameAsDealership) return 'Same as dealership';
  const line1 = [dealership.shippingAddress1, dealership.shippingAddress2].filter(Boolean).join(', ');
  const line2 = [dealership.shippingCity, dealership.shippingState, dealership.shippingPostalCode, dealership.shippingCountry].filter(Boolean).join(', ');
  return [line1, line2].filter(Boolean).join(' | ');
};
// [Shipping] END
const ACTIVE_DEALERSHIP_KEY = 'activeDealershipBac';
// • Helpers for localStorage persistence
const loadActiveDealershipBac = () => localStorage.getItem(ACTIVE_DEALERSHIP_KEY);
const storeActiveDealershipBac = (bac) => localStorage.setItem(ACTIVE_DEALERSHIP_KEY, bac);
const clearStoredDealershipBac = () => localStorage.removeItem(ACTIVE_DEALERSHIP_KEY);
const DealershipContext = createContext();

export const DealershipProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const { api } = useAuth();

  const [dealership, setDealershipState] = useState(null);
  const [dealerships, setDealerships] = useState([]); // Array of all dealerships for the user
  const [productDetails, setProductDetails] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Add a state to track if we've attempted to fetch dealership data
  const [dealershipFetchAttempted, setDealershipFetchAttempted] = useState(false);
  // BACs corrected this session, old -> new, so the cart can tell a renamed
  // dealership from a different one.
  const [renamedBacs, setRenamedBacs] = useState({});
  // The dealership as of the latest render, for code that carries on after
  // an await (its own closure still holds the one from before).
  const dealershipRef = useRef(dealership);
  dealershipRef.current = dealership;

  // • Set active dealership and persist selection
  const setActiveDealership = useCallback((dealer) => {
    const normalized = dealer ? normalizeDealership(dealer) : dealer;
    setDealershipState(normalized);
    if (normalized?.bac) {
      storeActiveDealershipBac(normalized.bac.toString());
    } else {
      clearStoredDealershipBac();
    }
    setDealershipFetchAttempted(false);
  }, []);


  const resetDealershipState = () => {
    setDealershipState(null);
    clearStoredDealershipBac();
    setDealerships([]);
    setProductDetails(null);
    setProductLoading(false);
    setProductError(null);
    setOrders([]);
    setOrdersLoading(false);
    setOrdersError(null);
    setLoading(false);
    setError(null);
    setDealershipFetchAttempted(false);
  };
  const fetchDealership = async () => {
    // Prevent redundant fetch attempts when no dealership exists
    if (loading || (dealershipFetchAttempted && !dealership)) {
      return;
    }

    setLoading(true);

    if (!user) {
      setDealershipState(null);
      setDealerships([]);
      setLoading(false);
      setDealershipFetchAttempted(true); // Mark as attempted even if no user
      return;
    }

    try {
      const response = await api.get(`/api/getDealershipsByUserId/${user.id}`);
      const dealershipsArray = response.data.dealerships;

      // Normalize BAC values from the server immediately
      const normalizedDealerships = (dealershipsArray || []).map(d => {
        const bac = parseBAC(d.bac ?? d.dealershipBac);
        return normalizeDealership({
          ...d,
          bac,
          dealershipBac: bac
        });
      });
      // Store the full list of dealerships
      setDealerships(normalizedDealerships);

      if (normalizedDealerships && normalizedDealerships.length > 0) {
        let initialDealer = normalizedDealerships[0];
        const storedBac = loadActiveDealershipBac();
        if (storedBac) {
          const match = normalizedDealerships.find(d => String(d.bac) === storedBac);
          if (match) initialDealer = match;
        }
        const dealershipDetails = await api.get(`/api/getDealershipDetails/${initialDealer.dealershipBac}`);
        // Store the dealership data and normalize BAC
        const dealershipRaw = dealershipDetails.data.dealership || {};
        const detailBac = parseBAC(dealershipRaw.bac ?? dealershipRaw.dealershipBac);
        const dealershipData = normalizeDealership({
          ...dealershipRaw,
          bac: detailBac,
          dealershipBac: detailBac,
        });

        // Fetch orders for this dealership
        try {
          setOrdersLoading(true);
          const ordersResponse = await api.get(`/api/getAllDealerOrders/${bacToString(dealershipData.bac)}`);

          // Normalize BAC on each order before storing
          const normalizedOrders = (ordersResponse.data.orders || []).map(o => {
            const orderBac = parseBAC(o.bac ?? o.dealershipBac);
            return { ...o, bac: orderBac, dealershipBac: orderBac };
          });
          // Set the orders on the dealership object directly
          dealershipData.orders = normalizedOrders;

          // Now set the dealership with orders included
          setActiveDealership(dealershipData);

          // Also update the separate orders state for compatibility
          setOrders(normalizedOrders);
       } catch (orderErr) {
          // Failed to fetch orders
          dealershipData.orders = [];
          setActiveDealership(dealershipData);
          setOrders([]);
          setOrdersError('Failed to load orders');
        } finally {
          setOrdersLoading(false);
        }
      } else {
        setActiveDealership(null);
        setOrders([]);
        setOrdersError(null);
        setOrdersLoading(false);
      }
    } catch (err) {
      setError('An error occurred while fetching dealership data.');
      setDealershipState(null);
      clearStoredDealershipBac();
    } finally {
      setLoading(false);
      // Mark as attempted regardless of success or failure
      setDealershipFetchAttempted(true);
    }
  };
    useEffect(() => {
    // Only fetch if we have a user and haven't attempted to fetch yet
    if (!user) {
      resetDealershipState();
    }
    if (user && !dealershipFetchAttempted && !loading) {
      // This effect depends on `loading`; fetchDealership also updates `loading`.
      // The early return inside fetchDealership prevents an infinite loop.
      fetchDealership();
    }
  }, [user, dealershipFetchAttempted, loading]);

  // • Fallback if active dealership is removed or becomes inaccessible
  useEffect(() => {
    if (dealership && dealerships.length) {
      const exists = dealerships.some(d => d.bac === dealership.bac);
      if (!exists) {
        setActiveDealership(dealerships[0] || null);
      }
    }
  }, [dealerships, dealership, setActiveDealership]);

  // Reset fetch attempted flag when user changes
  useEffect(() => {
    setDealershipFetchAttempted(false);
  }, [user?.id]); // Only reset when user ID changes

  // Function to manually refresh dealership data
  const refreshDealership = () => {
    setDealershipFetchAttempted(false); // Reset the flag to trigger a new fetch
  };

  // Get product details function
  const getProductDetails = useCallback(async (productId) => {
    if (!productId) return;

    setProductLoading(true);
    setProductError(null);

    try {
      // In a real implementation, this would be an API call
      const response = await api.get(`/api/getItem/${productId}`);

      if (response.data.item) {
        setProductDetails(response.data.item);
      } else {
        setProductError(`No product details found for ID: ${productId}`);
        setProductDetails(null);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load product details';
      setProductError(errorMessage);
      setProductDetails(null);
    } finally {
      setProductLoading(false);
    }
  }, [api]);

  // Clear product details when needed
  const clearProductDetails = useCallback(() => {
    setProductDetails(null);
    setProductError(null);
  }, []);


  // Get orders function - now primarily used for refreshing orders
  const getOrders = useCallback(async () => {
    if (!dealership?.bac) return;

    setOrdersLoading(true);
    setOrdersError(null);

    try {
      const response = await api.get(`/api/getAllDealerOrders/${bacToString(dealership.bac)}`);

      // Normalize BAC on orders before updating state
      const normalizedOrders = (response.data.orders || []).map(o => {
        const orderBac = parseBAC(o.bac ?? o.dealershipBac);
        return { ...o, bac: orderBac, dealershipBac: orderBac };
      });
      // Update both the separate orders state and the dealership.orders
      setOrders(normalizedOrders);

      // Also update the orders on the dealership object
      setDealershipState(prevDealership => {
        if (prevDealership) {
          return { ...prevDealership, orders: normalizedOrders };
        }
        return prevDealership;
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load orders';
      setOrdersError(errorMessage);
    } finally {
      setOrdersLoading(false);
    }
  }, [api, dealership]);

  const createDealership = async (dealershipData) => {
    try {
      const response = await api.post('/api/createDealer', dealershipData);

      // Normalize BAC on the created dealership
      const newDealer = normalizeDealership({
        ...response.data.dealership,
        bac: parseBAC(response.data.dealership?.bac ?? response.data.dealership?.dealershipBac)
      });
      newDealer.dealershipBac = newDealer.bac;

      setActiveDealership(newDealer);
      // Update the dealerships array
      setDealerships(prev => [...prev, newDealer]);

      setDealershipFetchAttempted(true); // Mark as attempted after creation
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'An error occurred while creating dealership.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // { refetch: false } is for a save that a BAC change follows (Edit
  // Dealership). A full reload started here would still be running when the
  // dealership moves: it hid the form, so a refused BAC was never shown, and
  // a reload that asked for the old BAC after the move was refused and left
  // no dealership. The saved details are shown in place instead, and
  // changeDealershipBac reloads once the move is done.
  const updateDealership = async (updatedData, { refetch = true } = {}) => {
    try {
    const response = await api.put(`/api/updateDealer/${bacToString(dealership.bac)}`, updatedData);

      // Normalize BAC on the updated dealership before storing
      const updatedDealer = normalizeDealership({
        ...response.data.dealership,
        bac: parseBAC(response.data.dealership?.bac ?? response.data.dealership?.dealershipBac)
      });
      updatedDealer.dealershipBac = updatedDealer.bac;
      if (refetch) {
        setActiveDealership(updatedDealer);
      } else {
        setDealershipState((prev) => {
          if (!prev) return updatedDealer;
          const bac = updatedDealer.bac ?? prev.bac;
          return { ...prev, ...updatedDealer, bac, dealershipBac: bac, orders: prev.orders };
        });
      }

      // Update the dealership in the dealerships array
      setDealerships(prev =>
        prev.map(d =>
          d.bac === updatedDealer.bac ? updatedDealer : d
        )
      );

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'An error occurred while updating dealership.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // A dealer correcting their dealership's BAC (Lee, 2026-10-02). The server
  // moves the dealership, its orders, employees and logins to the new BAC.
  // Here it is renamed in place and remembered under the new BAC, which also
  // fetches it again; renamedBacs lets the cart keep what is in it.
  const changeDealershipBac = async (fromBac, newBac) => {
    const from = bacToString(fromBac);
    let nextBac;
    try {
      const response = await api.put(`/api/dealerships/${encodeURIComponent(from)}/bac`, { bac: String(newBac) });
      nextBac = parseBAC(response.data?.dealership?.bac);
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error
          || 'The BAC could not be changed. Please try again or contact Martin Advertising.',
      };
    }
    if (nextBac === undefined) {
      // Changed on the server, but the answer was unreadable: fetch it all again.
      refreshDealership();
      return { success: true, bac: undefined };
    }
    const renamed = (d) => normalizeDealership({ ...d, bac: nextBac, dealershipBac: nextBac });
    setRenamedBacs((prev) => ({ ...prev, [from]: nextBac }));
    setDealerships((prev) => prev.map((d) => (bacToString(d.bac) === from ? renamed(d) : d)));
    // The latest dealership, not this closure's: it has the details saved
    // just before the move.
    const current = dealershipRef.current;
    if (current && bacToString(current.bac) === from) {
      setActiveDealership(renamed(current));
    }
    return { success: true, bac: nextBac };
  };

  const saveDealershipLogo = async (dealershipBac, file) => {
    const bacString = bacToString(dealershipBac);
    if (!bacString) {
      return { success: false, error: 'Missing dealership identifier.' };
    }

    if (!file) {
      return { success: false, error: 'Logo file is required.' };
    }

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.put(
        `/api/dealerships/${encodeURIComponent(bacString)}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      const newLogoUrl = response.data?.logoUrl ?? null;

      if (newLogoUrl !== null) {
        setDealershipState((prev) => {
          if (!prev) return prev;
          const prevBac = bacToString(prev.bac);
          return prevBac === bacString ? { ...prev, logoUrl: newLogoUrl } : prev;
        });

        setDealerships((prev) =>
          prev.map((dealer) => {
            const dealerBac = bacToString(dealer.bac);
            return dealerBac === bacString ? { ...dealer, logoUrl: newLogoUrl } : dealer;
          }),
        );
      }

      return { success: true, logoUrl: newLogoUrl };
    } catch (err) {
      console.error('[Logo]', err);
      const errorMessage = err.response?.data?.error || 'Failed to save dealership logo.';
      return { success: false, error: errorMessage };
    }
  };

  const removeDealershipLogo = async (dealershipBac) => {
    const bacString = bacToString(dealershipBac);
    if (!bacString) {
      return { success: false, error: 'Missing dealership identifier.' };
    }

    try {
      await api.delete(`/api/dealerships/${encodeURIComponent(bacString)}/logo`);

      setDealershipState((prev) => {
        if (!prev) return prev;
        const prevBac = bacToString(prev.bac);
        return prevBac === bacString ? { ...prev, logoUrl: null } : prev;
      });

      setDealerships((prev) =>
        prev.map((dealer) => {
          const dealerBac = bacToString(dealer.bac);
          return dealerBac === bacString ? { ...dealer, logoUrl: null } : dealer;
        }),
      );

      return { success: true };
    } catch (err) {
      console.error('[Logo]', err);
      const errorMessage = err.response?.data?.error || 'Failed to remove dealership logo.';
      return { success: false, error: errorMessage };
    }
  };

  return (
    <DealershipContext.Provider
      value={{
        dealership,
        dealerships, // Expose the dealerships array
        setActiveDealership,
        createDealership,
        loading,
        dealershipFetchAttempted,
        error,
        updateDealership,
        changeDealershipBac,
        renamedBacs,
        fetchDealership,
        refreshDealership,
        setError,
        saveDealershipLogo,
        removeDealershipLogo,
        // Product-related values
        productDetails,
        productLoading,
        productError,
        getProductDetails,
        clearProductDetails,
        // Order-related values
        orders,
        ordersLoading,
        ordersError,
        getOrders,
        // Flag for tracking fetch attempts
        dealershipFetchAttempted,
        //reset Dealership state
        resetDealershipState,
         // [Shipping] BEGIN
        formatShippingAddress,
        // [Shipping] END
      }}
    >
      {children}
    </DealershipContext.Provider>
  );
};

// Add the useDealership hook
export const useDealership = () => {
  const context = useContext(DealershipContext);
  if (!context) {
    throw new Error('useDealership must be used within a DealershipProvider');
  }
  return context;
};

//  subscribe to dealership changes
export const useDealershipChange = (callback) => {
  const { dealership } = useDealership();
  const prevBacRef = useRef();
  useEffect(() => {
    if (dealership?.bac !== prevBacRef.current) {
      prevBacRef.current = dealership?.bac;
      if (callback) callback(dealership);
    }
  }, [dealership, callback]);
};

export default DealershipContext;
